/**
 * Story 564, settings.section.task_completion: whether finishing a task in this
 * project needs a commit, on the Task completion section of the settings page.
 *
 * Moved from the Project settings dialog (story 556) with its behaviour
 * unchanged. Story 540: the setting applies the moment it is changed. Each
 * write carries the value the section believes is in force, which follows the
 * server: a change made elsewhere comes back as a conflict, and after that the
 * belief is the server's value. A refused write never leaves the switch showing
 * a value that is not in force.
 *
 * Who may change it is the page's concern: it shows the one owner-only note and
 * passes readOnly; a write refused for role is handed back with onRefusedForRole.
 */

import { useCallback, useState } from 'react';
import { useUpdateCommitRequirement, ProjectSettingsRequestError } from '../../hooks/useProjectSettings';
import type { SettingsSectionProps } from './settingsSections';
import {
  COMMIT_LEGEND,
  COMMIT_BENEFIT_WHO,
  COMMIT_BENEFIT_HONEST,
  COMMIT_APPLIED_ON,
  COMMIT_APPLIED_OFF,
  COMMIT_SAVE_FAILED_MESSAGE,
  COMMIT_CONFLICT_MESSAGE,
} from '../workspace/projectSettingsCopy';

const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;
const COMMIT_LABEL_ID = 'project-settings-commit-label';
const COMMIT_BENEFITS_ID = 'project-settings-commit-benefits';

export function TaskCompletionSection({ projectSlug, settings, readOnly, onRefusedForRole }: SettingsSectionProps) {
  const update = useUpdateCommitRequirement(projectSlug);

  /** The value the section believes is in force; sent as expected_previous_commit. */
  const [inForce, setInForce] = useState<boolean>(settings.commit_evidence_required);
  /** What the switch shows; differs from inForce only while a write is in flight. */
  const [shown, setShown] = useState<boolean>(settings.commit_evidence_required);
  const [appliedMessage, setAppliedMessage] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const handleToggle = useCallback(async () => {
    if (readOnly || update.isPending) return;
    const next = !inForce;
    setShown(next);
    setFailure(null);
    setConflict(false);
    setAppliedMessage(null);
    setWarning(null);
    try {
      const change = await update.mutateAsync({ commit_evidence_required: next, expected_previous_commit: inForce });
      setInForce(change.commit_evidence_required);
      setShown(change.commit_evidence_required);
      setAppliedMessage(change.commit_evidence_required ? COMMIT_APPLIED_ON : COMMIT_APPLIED_OFF);
      if (change.warning) setWarning(change.warning);
    } catch (err) {
      if (err instanceof ProjectSettingsRequestError && err.status === HTTP_CONFLICT && typeof err.body.current_commit === 'boolean') {
        // Someone else changed it first: show what is in force, and make the
        // next change a change to that rather than to the stale value.
        setInForce(err.body.current_commit);
        setShown(err.body.current_commit);
        setConflict(true);
        return;
      }
      setShown(inForce);
      if (err instanceof ProjectSettingsRequestError && err.status === HTTP_FORBIDDEN) {
        onRefusedForRole();
        return;
      }
      setFailure(COMMIT_SAVE_FAILED_MESSAGE);
    }
  }, [readOnly, inForce, update, onRefusedForRole]);

  return (
    <>
      <div className="project-settings__option" role="group" aria-labelledby={COMMIT_LABEL_ID}>
        <div className="project-settings__switch-row">
          <span id={COMMIT_LABEL_ID} className="project-settings__choice">{COMMIT_LEGEND}</span>
          <button
            type="button"
            role="switch"
            aria-checked={shown}
            aria-labelledby={COMMIT_LABEL_ID}
            aria-describedby={COMMIT_BENEFITS_ID}
            disabled={readOnly}
            className="project-settings__switch"
            onClick={() => void handleToggle()}
          />
        </div>
        <ul id={COMMIT_BENEFITS_ID} className="project-settings__guidance">
          <li>{COMMIT_BENEFIT_WHO}</li>
          <li>{COMMIT_BENEFIT_HONEST}</li>
        </ul>
      </div>

      {appliedMessage && <p className="project-settings__status" role="status">{appliedMessage}</p>}
      {warning && <p className="project-settings__status" role="status">{warning}</p>}
      {conflict && <p className="project-settings__status" role="status">{COMMIT_CONFLICT_MESSAGE}</p>}
      {failure && <p className="project-settings__error" role="alert">{failure}</p>}
    </>
  );
}
